import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TablePagination,
  Stack,
  Divider,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Phone as PhoneIcon,
  CallMade as OutboundIcon,
  CallReceived as InboundIcon,
  SwapHoriz as InternalIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Timer as TimerIcon,
  PhoneMissed as MissedIcon,
  CheckCircle as AnsweredIcon,
  FiberManualRecord as RecordIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  Close as CloseIcon,
  Summarize as SummarizeIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import callHistoryService from '../../services/callHistory.service';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { formatInUserTimezone } from '../../utils/dateTimeUtils';
import errorLogger from '../../services/errorLogger.service';

const logger = errorLogger.createComponentLogger('CallHistory');

interface CallRecord {
  id: number;
  extension: string;
  phone_number?: string;
  incoming_did?: string;
  extension_name: string;
  direction: 'inbound' | 'outbound' | 'internal';
  remote_number: string;
  caller_id_name: string;
  start_time: string;
  end_time: string;
  duration: number;
  duration_formatted: string;
  disposition: string;
  rec_uniqueid?: string;
  wait_time?: number;
  is_transfer: boolean;
  transferred_from?: string;
  is_callback: boolean;
  area_code?: string;
  recording?: {
    id: number;
    size: number;
    storage: string;
    url: string;
  };
  // Legacy fields
  recording_path?: string;
  recording_filename?: string;
  should_record?: number;
  recording_rule_id?: number;
  recording_status?: 'not_recorded' | 'recording' | 'recorded' | 'failed';
}

interface CallStatistics {
  total_calls: number;
  inbound_calls: number;
  outbound_calls: number;
  internal_calls: number;
  total_duration: number;
  total_duration_formatted: string;
  inbound_duration: number;
  inbound_duration_formatted: string;
  avg_inbound_duration: number;
  avg_inbound_duration_formatted: string;
  outbound_duration: number;
  outbound_duration_formatted: string;
  avg_outbound_duration: number;
  avg_outbound_duration_formatted: string;
  internal_duration: number;
  internal_duration_formatted: string;
  avg_internal_duration: number;
  avg_internal_duration_formatted: string;
  avg_duration: number;
  avg_duration_formatted: string;
  answered_calls: number;
  missed_calls: number;
  busy_calls: number;
  unique_extensions: number;
  unique_numbers: number;
  answer_rate: number;
}

interface ActiveCall {
  id: number;
  custid: string;
  extension: string;
  phone_number?: string;
  incoming_did?: string;
  call_direction: string;
  remote_number: string;
  start_time: string;
  channel_id: string;
  caller_id_name: string;
  duration_seconds: number;
}

const CallHistory: React.FC = () => {
  const { member, isLoginAsCustomer } = useAuthWithCustomerOverride();
  const customerFilter = useCustomerFilter();
  
  // Check for login token on initialization
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = !!sessionStorage.getItem('loginAsCustomer');
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.debug('Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });
  
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [activeCalls, setActiveCalls] = useState<ActiveCall[]>([]);
  const [statistics, setStatistics] = useState<CallStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Helper function to create a date in local timezone
  const createLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  
  // Filters - Default to today
  const [startDate, setStartDate] = useState<Date | null>(startOfDay(new Date()));
  const [endDate, setEndDate] = useState<Date | null>(endOfDay(new Date()));
  const [extension, setExtension] = useState('');
  const [direction, setDirection] = useState<'' | 'inbound' | 'outbound' | 'internal'>('');
  const [department, setDepartment] = useState('');
  const [site, setSite] = useState('');
  const [searchNumber, setSearchNumber] = useState('');

  // Available filter options
  const [availableDepartments, setAvailableDepartments] = useState<string[]>([]);
  const [availableSites, setAvailableSites] = useState<string[]>([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Auto-refresh for active calls
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Track playing recordings
  const [playingRecordingId, setPlayingRecordingId] = useState<number | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [audioDebugInfo, setAudioDebugInfo] = useState<string>('');

  // Detect if mobile device (based on user agent only, not screen size)
  const [isMobile, setIsMobile] = useState(false);

  // Summary report state
  const [summarizeBy, setSummarizeBy] = useState<'extension' | 'user'>('extension');
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      // Only use user agent for detection - more reliable than screen width
      const mobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
      logger.debug('Mobile detection:', { mobile, userAgent: navigator.userAgent });

      // Show alert to confirm detection
      if (mobile) {
        console.log('✅ MOBILE DEVICE DETECTED:', navigator.userAgent);
      } else {
        console.log('💻 DESKTOP DEVICE DETECTED:', navigator.userAgent);
      }
    };

    checkMobile();
  }, []);

  const fetchCallHistory = useCallback(async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Still waiting for login-as data, aborting fetch');
      return;
    }
    
    // Verify session data matches member if in login-as mode
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member?.customerID) {
          logger.error('CRITICAL: Customer ID mismatch!', {
            sessionCustomerId: parsed.customerId,
            memberCustomerId: member?.customerID
          });
          setError('Customer context mismatch - please refresh the page');
          setLoading(false);
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Apply customer filter
      const params: any = {
        start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
        extension: extension || undefined,
        direction: direction || undefined,
        department: department || undefined,
        site: site || undefined,
        remote_number: searchNumber || undefined,
        page: page + 1,
        per_page: rowsPerPage
      };

      // Add customer filter for admins
      if (isLoginAsCustomer && member?.customerID) {
        logger.debug('LOGIN AS MODE - Using customerID:', member.customerID);
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        logger.debug('FILTER MODE - Using customerFilter:', customerFilter.customerId);
        params.customerId = customerFilter.customerId;
      } else {
        logger.debug('NO FILTER - Will use JWT customer');
      }

      const response = await callHistoryService.getCallHistory(params);

      if (response.success) {
        // Debug: Log the raw response
        console.log('[CallHistory] Raw API response:', response.data);
        console.log('[CallHistory] Number of calls received:', response.data.calls?.length);

        // Ensure we're not duplicating data
        const uniqueCalls = response.data.calls || [];
        console.log('[CallHistory] Setting calls:', uniqueCalls);

        setCalls(uniqueCalls);
        setStatistics(response.data.statistics);
        setTotalCount(response.data.pagination.total);

        // Update available filter options
        setAvailableDepartments((response.data as any).available_departments || []);
        setAvailableSites((response.data as any).available_sites || []);
      } else {
        setError('Failed to load call history');
      }
    } catch (err) {
      logger.error('Error fetching call history:', err);
      setError('Error loading call history');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, extension, direction, department, site, searchNumber, page, rowsPerPage, isLoginAsCustomer, member?.customerID, customerFilter.isFiltered, customerFilter.customerId, member?.memberType, waitingForLoginAs]);

  const fetchActiveCalls = useCallback(async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Still waiting for login-as data, aborting active calls fetch');
      return;
    }
    
    try {
      // Apply customer filter to active calls
      const params: any = {};
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }
      
      const response = await callHistoryService.getActiveCalls(params);
      
      if (response.success) {
        const newActiveCalls = response.data.activeCalls || [];
        
        setActiveCalls(prevActiveCalls => {
          // Check if any calls ended (were in previous list but not in new list)
          if (prevActiveCalls.length > 0 && newActiveCalls.length < prevActiveCalls.length) {
            // A call has ended, refresh the call history
            logger.debug('Call ended, refreshing call history and statistics...');
            // Directly call fetchCallHistory to ensure statistics update
            setTimeout(() => {
              fetchCallHistory();
            }, 1000); // Small delay to ensure DB is updated
            // Check for recordings after 5 seconds, then 30, then 60
            setTimeout(() => {
              checkForRecordingUpdates();
            }, 5000);
            setTimeout(() => {
              checkForRecordingUpdates();
            }, 30000);
            setTimeout(() => {
              checkForRecordingUpdates();
            }, 60000);
          }
          return newActiveCalls;
        });
      }
    } catch (err) {
      logger.error('Error fetching active calls:', err);
    }
  }, [isLoginAsCustomer, member?.customerID, customerFilter.isFiltered, customerFilter.customerId, member?.memberType, waitingForLoginAs]);

  const checkForRecordingUpdates = useCallback(async () => {
    try {
      logger.debug('Checking for recording updates...');
      const response = await callHistoryService.checkRecordingUpdates(5); // Check last 5 minutes
      
      if (response.success && response.data.recordings_linked > 0) {
        logger.debug(`Found ${response.data.recordings_linked} new recordings, refreshing statistics...`);
        // Refresh the call history to show the new recordings and update statistics
        fetchCallHistory();
      }
    } catch (err) {
      logger.error('Error checking for recording updates:', err);
    }
  }, []);

  useEffect(() => {
    if (!member) return;
    
    // Check for login token
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    
    // If we have a login token but no session data, wait
    if (hasLoginToken && !sessionData) {
      logger.debug('Have login token but no session data - waiting...');
      return;
    }
    
    // If session data exists, verify it matches member
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member.customerID) {
          logger.debug('Session/member mismatch, waiting for sync...');
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }
    
    // Add small delay to let state settle
    const timer = setTimeout(() => {
      fetchCallHistory();
    }, 200);
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, extension, direction, department, site, searchNumber, page, rowsPerPage, member?.customerID, isLoginAsCustomer]);

  useEffect(() => {
    if (!member || waitingForLoginAs) return;
    
    fetchActiveCalls();
    
    if (autoRefresh) {
      const interval = setInterval(fetchActiveCalls, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [fetchActiveCalls, autoRefresh, member, waitingForLoginAs]);

  // Listen for login-as-customer changes to update tab title and refetch data
  useEffect(() => {
    const handleLoginAsChange = (event: CustomEvent) => {
      if (event.detail && event.detail.customerId && event.detail.customerName) {
        document.title = `${event.detail.customerId} - ${event.detail.customerName} - Call History`;
        // Also refetch data when login-as changes
        setTimeout(() => {
          if (member) {
            fetchCallHistory();
            fetchActiveCalls();
          }
        }, 100);
      }
    };

    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);

    // Set initial title if in login-as mode
    if (isLoginAsCustomer && member) {
      const customerId = (member as any).customerID || (member as any).customer_id;
      const customerName = (member as any).loginAsCustomerName || '';
      if (customerId && customerName) {
        document.title = `${customerId} - ${customerName} - Call History`;
      }
    }

    return () => {
      window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);
      // Reset title when component unmounts
      if (!isLoginAsCustomer) {
        document.title = 'Portal';
      }
    };
  }, [isLoginAsCustomer, member]);
  
  // Check for recording updates every 30 seconds for recent calls
  useEffect(() => {
    const recordingCheckInterval = setInterval(() => {
      // Check if there are recent calls that might not have recordings yet
      const hasRecentCalls = calls.some(call => {
        const callTime = new Date(call.start_time).getTime();
        const isRecent = callTime > Date.now() - 15 * 60 * 1000; // Last 15 minutes
        const hasNoRecording = !call.recording || 
                               call.recording_status === 'not_recorded' || 
                               call.recording_status === null;
        return isRecent && hasNoRecording && call.duration > 0;
      });
      
      if (hasRecentCalls) {
        logger.debug('Checking for new recordings...');
        checkForRecordingUpdates();
      }
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(recordingCheckInterval);
  }, [calls, checkForRecordingUpdates]);

  // Listen for login-as-customer changes to update tab title
  useEffect(() => {
    const handleLoginAsChange = (event: CustomEvent) => {
      if (event.detail && event.detail.customerId && event.detail.customerName) {
        document.title = `${event.detail.customerId} - ${event.detail.customerName} - Call History`;
      }
    };

    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);

    // Set initial title if in login-as mode
    if (isLoginAsCustomer && member) {
      const customerId = (member as any).customerID || (member as any).customer_id;
      const customerName = (member as any).loginAsCustomerName || '';
      if (customerId && customerName) {
        document.title = `${customerId} - ${customerName} - Call History`;
      }
    }

    return () => {
      window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);
      // Reset title when component unmounts
      if (!isLoginAsCustomer) {
        document.title = 'Portal';
      }
    };
  }, [isLoginAsCustomer, member]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    fetchCallHistory();
  };

  const handleViewSummary = async () => {
    try {
      setSummaryLoading(true);
      setError(null);

      const params: any = {
        start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
        extension: extension || undefined,
        direction: direction || undefined,
        department: department || undefined,
        site: site || undefined,
        remote_number: searchNumber || undefined,
        summarize_by: summarizeBy
      };

      // Add customer filter for admins
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }

      const response = await api.get('/endpoints/call-history-summary.php', { params });

      if (response.data.success) {
        setSummaryData(response.data.data.summary);
        setSummaryDialogOpen(true);
      } else {
        setError(response.data.message || 'Failed to load summary');
      }
    } catch (err) {
      logger.error('Error fetching summary:', err);
      setError('Error loading summary data');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setSummaryLoading(true);

      // Build query params for PDF export
      const params: any = {
        start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
        extension: extension || undefined,
        direction: direction || undefined,
        department: department || undefined,
        site: site || undefined,
        remote_number: searchNumber || undefined,
        summarize_by: summarizeBy
      };

      // Add customer filter for admins
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && customerFilter.customerId && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }

      // Fetch HTML from API with authentication
      const response = await api.get('/endpoints/call-history-summary-pdf.php', {
        params,
        responseType: 'text'
      });

      // Open HTML in new window
      const pdfWindow = window.open('', '_blank');
      if (pdfWindow) {
        pdfWindow.document.write(response.data);
        pdfWindow.document.close();
      } else {
        setError('Please allow pop-ups to view the PDF report');
      }
    } catch (err) {
      logger.error('Error exporting PDF:', err);
      setError('Error generating PDF report');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setSummaryLoading(true);

      // Build query params for Excel/CSV export
      const params: any = {
        start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
        extension: extension || undefined,
        direction: direction || undefined,
        department: department || undefined,
        site: site || undefined,
        remote_number: searchNumber || undefined,
        summarize_by: summarizeBy
      };

      // Add customer filter for admins
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && customerFilter.customerId && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }

      // Fetch CSV from API with authentication
      const response = await api.get('/endpoints/call-history-summary-excel.php', {
        params,
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename
      const dateStr = startDate && endDate
        ? `${format(startDate, 'yyyy-MM-dd')}_to_${format(endDate, 'yyyy-MM-dd')}`
        : format(new Date(), 'yyyy-MM-dd');
      link.download = `call_history_summary_${dateStr}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage('Excel file downloaded successfully');
    } catch (err) {
      logger.error('Error exporting Excel:', err);
      setError('Error generating Excel file');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handlePlayRecording = async (call: CallRecord) => {
    logger.debug('=== PLAY RECORDING CLICKED (UPDATED VERSION) ===');
    logger.debug('Call object:', call);

    if (!call.recording) {
      logger.debug('No recording found in call object');
      return;
    }

    // If this recording is already playing, stop it
    if (playingRecordingId === call.recording.id) {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      setPlayingRecordingId(null);
      setAudioUrl(null);
      return;
    }

    // Stop any currently playing audio
    if (playingRecordingId) {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    }

    try {
      let newAudioUrl: string;

      logger.debug('Recording URL:', call.recording.url);
      logger.debug('Recording ID:', call.recording.id);

      // Check if this is already a shareable link (starts with /r/)
      if (call.recording.url.includes('/r/')) {
        // Convert shareable URL to direct API URL for production compatibility
        const token = call.recording.url.split('/r/')[1];
        newAudioUrl = `/api/v1/endpoints/recording.php?token=${token}`;
        logger.debug('Converting shareable URL to API URL:', newAudioUrl);
      } else {
        // For API URLs, get the proper audio URL from the recording-playback API
        const response = await api.get(`/endpoints/recording-playback.php?id=${call.recording.id}`);

        if (!response.data.success) {
          setError(response.data.message || 'Failed to load recording');
          return;
        }

        // Handle different response types from the API
        if (response.data.data.type === 'url') {
          newAudioUrl = response.data.data.url;
        } else if (response.data.data.type === 'data') {
          newAudioUrl = response.data.data.dataUrl;
        } else if (response.data.data.type === 'stream') {
          newAudioUrl = response.data.data.streamUrl;
        } else {
          setError('Unsupported recording format');
          return;
        }

        logger.debug('Using API-generated URL:', newAudioUrl);
      }

      logger.debug('Final audio URL:', newAudioUrl);

      // Set the audio URL and recording ID - playback will happen in useEffect
      setAudioUrl(newAudioUrl);
      setPlayingRecordingId(call.recording.id);

    } catch (err) {
      logger.error('Error playing recording:', err);
      setError('Failed to load recording');
      setPlayingRecordingId(null);
      setAudioUrl(null);
    }
  };

  const handleDownloadRecording = async (call: CallRecord) => {
    if (!call.recording) return;
    
    try {
      // Fetch the recording and create a blob URL for download
      const response = await fetch(call.recording.url, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch recording');
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `recording_${call.id}_${call.start_time.split(' ')[0]}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (err) {
      logger.error('Error downloading recording:', err);
      setError('Failed to download recording');
    }
  };
  
  const handleCopyRecordingLink = async (call: CallRecord) => {
    if (!call.recording) return;
    
    try {
      // Copy the shareable URL to clipboard
      await navigator.clipboard.writeText(call.recording.url);
      
      // Show success message
      setSuccessMessage('Recording link copied to clipboard!');
    } catch (err) {
      logger.error('Error copying link:', err);
      setError('Failed to copy recording link');
    }
  };

  const getDispositionColor = (disposition: string, duration?: number) => {
    const disp = disposition?.toUpperCase() || '';
    
    // Empty disposition = IVR hangup
    if (!disp) {
      return 'default';  // Gray for IVR hangups
    }
    
    // Special case: BUSY with long duration = likely voicemail
    if (disp === 'BUSY' && duration && duration > 20) {
      return 'info';  // Blue color for voicemail
    }
    
    switch (disp) {
      case 'ANSWERED':
      case 'ANSWER':
        return 'success';
      case 'NO ANSWER':
      case 'NOANSWER':
        return 'warning';
      case 'BUSY':
        return 'error';
      case 'FAILED':
      case 'CHANUNAVAIL':
        return 'error';
      case 'CANCEL':
        return 'default';
      case 'CONGESTION':
        return 'error';
      default:
        return 'default';
    }
  };
  
  const getDisplayDisposition = (disposition: string, duration: number) => {
    const disp = disposition?.toUpperCase() || '';
    
    // Empty disposition = likely hung up in IVR
    if (!disp || disp === 'UNKNOWN') {
      return 'IVR-Hangup';
    }
    
    // If BUSY with significant duration, likely voicemail without message
    if (disp === 'BUSY' && duration > 20) {
      return 'VM-No Message';
    }
    
    // Clean up disposition names for display
    switch (disp) {
      case 'ANSWERED':
      case 'ANSWER':
        return 'Answered';
      case 'NOANSWER':
        return 'No Answer';
      case 'BUSY':
        return 'Busy';
      case 'CANCEL':
        return 'Cancelled';
      case 'CHANUNAVAIL':
        return 'Unavailable';
      case 'CONGESTION':
        return 'Network Busy';
      case 'FAILED':
        return 'Failed';
      default:
        return disposition;
    }
  };

  const getDirectionIcon = (direction: string) => {
    switch (direction) {
      case 'inbound':
        return <InboundIcon color="primary" fontSize="small" />;
      case 'outbound':
        return <OutboundIcon color="secondary" fontSize="small" />;
      case 'internal':
        return <InternalIcon color="info" fontSize="small" />;
      default:
        return <PhoneIcon fontSize="small" />;
    }
  };

  return (
    <Box>
      {/* Active Calls Section */}
      {activeCalls.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                <PhoneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Active Calls ({activeCalls.length})
              </Typography>
              <FormControl size="small">
                <Select
                  value={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.value === 'true')}
                  displayEmpty
                >
                  <MenuItem value="true">Auto-refresh ON</MenuItem>
                  <MenuItem value="false">Auto-refresh OFF</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Extension</TableCell>
                    <TableCell>Phone Number</TableCell>
                    <TableCell>Direction</TableCell>
                    <TableCell>Remote Number</TableCell>
                    <TableCell>Caller ID</TableCell>
                    <TableCell>Duration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeCalls.map((call) => (
                    <TableRow key={call.id}>
                      <TableCell>
                        {/* Show extension if it's set, otherwise show dash */}
                        {call.extension || '-'}
                      </TableCell>
                      <TableCell>
                        {/* For inbound calls, show the DID; for outbound show phone_number */}
                        {call.call_direction === 'inbound' 
                          ? (call.incoming_did || call.extension || '-')
                          : (call.phone_number || '-')}
                      </TableCell>
                      <TableCell>
                        {getDirectionIcon(call.call_direction)}
                        {call.call_direction}
                      </TableCell>
                      <TableCell>{call.remote_number}</TableCell>
                      <TableCell>{call.caller_id_name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          icon={<TimerIcon />}
                          label={`${Math.floor(call.duration_seconds / 60)}:${(call.duration_seconds % 60).toString().padStart(2, '0')}`}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Call History Filters</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate ? format(startDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => setStartDate(e.target.value ? createLocalDate(e.target.value) : null)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="End Date"
                type="date"
                value={endDate ? format(endDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => setEndDate(e.target.value ? createLocalDate(e.target.value) : null)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Direction</InputLabel>
                <Select
                  value={direction}
                  onChange={(e) => setDirection(e.target.value as any)}
                  label="Direction"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="inbound">Inbound</MenuItem>
                  <MenuItem value="outbound">Outbound</MenuItem>
                  <MenuItem value="internal">Internal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  label="Department"
                >
                  <MenuItem value="">All</MenuItem>
                  {availableDepartments.map((dept) => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Site</InputLabel>
                <Select
                  value={site}
                  onChange={(e) => setSite(e.target.value)}
                  label="Site"
                >
                  <MenuItem value="">All</MenuItem>
                  {availableSites.map((siteOption) => (
                    <MenuItem key={siteOption} value={siteOption}>{siteOption}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Extension"
                value={extension}
                onChange={(e) => setExtension(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Search Number"
                value={searchNumber}
                onChange={(e) => setSearchNumber(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ mr: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setExtension('');
                  setDirection('');
                  setDepartment('');
                  setSite('');
                  setSearchNumber('');
                  setStartDate(startOfDay(subDays(new Date(), 7)));
                  setEndDate(startOfDay(new Date()));
                  setPage(0);
                }}
              >
                Clear Filters
              </Button>
            </Grid>

            {/* Summary Report Controls */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Summarize By</InputLabel>
                  <Select
                    value={summarizeBy}
                    onChange={(e) => setSummarizeBy(e.target.value as 'extension' | 'user')}
                    label="Summarize By"
                  >
                    <MenuItem value="extension">Extension</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={handleViewSummary}
                  startIcon={<ViewIcon />}
                  disabled={summaryLoading}
                  sx={{ mr: 1 }}
                >
                  View Summary
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<PdfIcon />}
                  onClick={handleExportPDF}
                  disabled={summaryLoading}
                  sx={{ mr: 1 }}
                >
                  Export PDF
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<ExcelIcon />}
                  onClick={handleExportExcel}
                  disabled={summaryLoading}
                >
                  Export Excel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Calls</Typography>
                <Typography variant="h4">{statistics.total_calls}</Typography>
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Chip
                    icon={<InboundIcon />}
                    label={`In: ${statistics.inbound_calls}`}
                    size="small"
                    color="primary"
                  />
                  <Chip
                    icon={<OutboundIcon />}
                    label={`Out: ${statistics.outbound_calls}`}
                    size="small"
                    color="secondary"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Answer Rate</Typography>
                <Typography variant="h4">{statistics.answer_rate}%</Typography>
                <Stack direction="row" spacing={1} mt={1}>
                  <Chip
                    icon={<AnsweredIcon />}
                    label={`${statistics.answered_calls}`}
                    size="small"
                    color="success"
                  />
                  <Chip
                    icon={<MissedIcon />}
                    label={`${statistics.missed_calls}`}
                    size="small"
                    color="warning"
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Duration</Typography>
                <Typography variant="body1" fontWeight="medium" mb={1}>
                  {statistics.total_calls} Total - Tot: {statistics.total_duration_formatted} Avg: {statistics.avg_duration_formatted}
                </Typography>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" minWidth="80px">
                      <InboundIcon color="primary" fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="caption">{statistics.inbound_calls} In</Typography>
                    </Box>
                    <Typography variant="caption" fontWeight="medium">Tot: {statistics.inbound_duration_formatted}</Typography>
                    <Typography variant="caption" color="textSecondary">Avg: {statistics.avg_inbound_duration_formatted}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" minWidth="80px">
                      <OutboundIcon color="secondary" fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="caption">{statistics.outbound_calls} Out</Typography>
                    </Box>
                    <Typography variant="caption" fontWeight="medium">Tot: {statistics.outbound_duration_formatted}</Typography>
                    <Typography variant="caption" color="textSecondary">Avg: {statistics.avg_outbound_duration_formatted}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" minWidth="80px">
                      <InternalIcon color="info" fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="caption">{statistics.internal_calls} Int</Typography>
                    </Box>
                    <Typography variant="caption" fontWeight="medium">Tot: {statistics.internal_duration_formatted}</Typography>
                    <Typography variant="caption" color="textSecondary">Avg: {statistics.avg_internal_duration_formatted}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Unique Numbers</Typography>
                <Typography variant="h4">{statistics.unique_numbers}</Typography>
                <Typography variant="body2" color="textSecondary" mt={1}>
                  {statistics.unique_extensions} extensions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Call History Table */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Call History</Typography>
            <IconButton onClick={fetchCallHistory} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {isMobile ? (
                // Mobile: Card-based layout
                <Stack spacing={2}>
                  {calls.map((call, index) => {
                    console.log(`[CallHistory] Rendering call ${index}:`, {
                      id: call.id,
                      time: call.start_time,
                      extension: call.extension,
                      remote: call.remote_number
                    });
                    return (
                      <Card key={`${call.id}-${index}`} variant="outlined">
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Stack spacing={1.5}>
                            {/* Time and Direction */}
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="body2" fontWeight="bold">
                                {formatInUserTimezone(call.start_time, member?.timezone || 'America/Toronto')}
                              </Typography>
                              <Box display="flex" alignItems="center">
                                {getDirectionIcon(call.direction)}
                                <Typography variant="caption" ml={0.5}>
                                  {call.direction}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Numbers */}
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Remote: <strong>{call.remote_number}</strong>
                              </Typography>
                              {call.caller_id_name && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {call.caller_id_name}
                                </Typography>
                              )}
                            </Box>

                            {/* Extension and Phone Number */}
                            {(call.extension || call.phone_number || call.incoming_did) && (
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {call.extension && !call.extension.startsWith('SIP/') && call.extension.length <= 4 && (
                                  <Chip
                                    label={`Ext ${call.extension}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                {call.direction === 'inbound' && call.incoming_did && (
                                  <Chip
                                    label={call.incoming_did}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                {call.direction === 'outbound' && call.phone_number && (
                                  <Chip
                                    label={call.phone_number}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            )}

                            {/* Duration and Status */}
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Chip
                                label={call.duration_formatted}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={getDisplayDisposition(call.disposition, call.duration)}
                                size="small"
                                color={getDispositionColor(call.disposition, call.duration) as any}
                              />
                            </Box>

                            {/* Recording Actions */}
                            {call.recording && (
                              <Box display="flex" justifyContent="flex-end" gap={1}>
                                <Tooltip title={playingRecordingId === call.recording.id ? "Stop" : "Play"}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePlayRecording(call)}
                                    color="primary"
                                  >
                                    {playingRecordingId === call.recording.id ? (
                                      <StopIcon fontSize="small" />
                                    ) : (
                                      <PlayIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadRecording(call)}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Share">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopyRecordingLink(call)}
                                  >
                                    <ShareIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              ) : (
                // Desktop: Table layout
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date/Time</TableCell>
                        <TableCell>Extension</TableCell>
                        <TableCell>Phone Number</TableCell>
                        <TableCell>Direction</TableCell>
                        <TableCell>Remote Number</TableCell>
                        <TableCell>Caller ID</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Recording</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {calls.map((call, index) => {
                        console.log(`[CallHistory] Rendering call ${index}:`, {
                          id: call.id,
                          time: call.start_time,
                          extension: call.extension,
                          remote: call.remote_number
                        });
                        return (
                        <TableRow key={`${call.id}-${index}`}>
                        <TableCell>
                          {formatInUserTimezone(call.start_time, member?.timezone || 'America/Toronto')}
                        </TableCell>
                        <TableCell>
                          {/* Extension Column - show actual extension or indicate if not recorded */}
                          {call.extension && !call.extension.startsWith('SIP/') && call.extension.length <= 4 ? (
                            <Box>
                              <Typography variant="body2">{call.extension}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {call.extension_name}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* Phone Number Column - show the incoming DID for inbound calls, phone_number for outbound */}
                          {call.direction === 'inbound' && call.incoming_did ? (
                            <Typography variant="body2">{call.incoming_did}</Typography>
                          ) : call.direction === 'inbound' && call.extension.length >= 10 && /^\d+$/.test(call.extension) ? (
                            <Typography variant="body2">{call.extension}</Typography>
                          ) : call.direction === 'outbound' && call.phone_number ? (
                            <Typography variant="body2">{call.phone_number}</Typography>
                          ) : (
                            <Typography variant="body2" color="textSecondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            {getDirectionIcon(call.direction)}
                            <Typography variant="body2" ml={0.5}>
                              {call.direction}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{call.remote_number}</TableCell>
                        <TableCell>{call.caller_id_name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={call.duration_formatted}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getDisplayDisposition(call.disposition, call.duration)}
                            size="small"
                            color={getDispositionColor(call.disposition, call.duration) as any}
                          />
                        </TableCell>
                        <TableCell>
                          {call.recording ? (
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title={playingRecordingId === call.recording.id ? "Stop Recording" : "Play Recording"}>
                                <IconButton
                                  size="small"
                                  onClick={() => handlePlayRecording(call)}
                                  color="primary"
                                >
                                  {playingRecordingId === call.recording.id ? (
                                    <StopIcon fontSize="small" />
                                  ) : (
                                    <PlayIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download Recording">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadRecording(call)}
                                  color="default"
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy Recording Link">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyRecordingLink(call)}
                                  color="default"
                                >
                                  <ShareIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              No recording
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              )}

              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Audio player - visible on mobile, hidden on desktop */}
      {isMobile && audioUrl && playingRecordingId && (
        // Mobile: Show visible floating player
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: 3,
            p: 2,
            minWidth: 300,
            maxWidth: '90%'
          }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={2} alignItems="center">
              <RecordIcon color="error" />
              <Box flex={1}>
                <Typography variant="body2" fontWeight="bold">
                  Playing Recording
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  const audio = audioRef.current;
                  if (audio) {
                    audio.pause();
                    audio.currentTime = 0;
                  }
                  setPlayingRecordingId(null);
                  setAudioUrl(null);
                  setAudioDebugInfo('');
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
            {audioDebugInfo && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '10px' }}>
                {audioDebugInfo}
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {/* Actual audio element - always present but styled differently based on device */}
      {audioUrl && playingRecordingId && (
        <audio
          ref={audioRef}
          src={audioUrl}
          controls={isMobile}
          autoPlay
          crossOrigin="use-credentials"
          onEnded={() => {
            logger.debug('Playback finished');
            setPlayingRecordingId(null);
            setAudioUrl(null);
          }}
          onLoadedMetadata={(e) => {
            const target = e.currentTarget;
            // Ensure volume is at maximum
            target.volume = 1.0;
            target.muted = false;
            const info = `Loaded: duration=${target.duration.toFixed(1)}s`;
            setAudioDebugInfo(info);
            logger.debug('Audio loaded metadata:', {
              duration: target.duration,
              readyState: target.readyState,
              networkState: target.networkState,
              src: target.src,
              volume: target.volume,
              muted: target.muted
            });
          }}
          onCanPlay={(e) => {
            setAudioDebugInfo('Ready to play');
            logger.debug('Audio can play - starting playback');
          }}
          onPlay={(e) => {
            const target = e.currentTarget;
            setAudioDebugInfo(`Playing... vol=${target.volume.toFixed(2)} muted=${target.muted}`);
            logger.debug('Audio started playing', { volume: target.volume, muted: target.muted, paused: target.paused });
          }}
          onTimeUpdate={(e) => {
            const target = e.currentTarget;
            if (target.currentTime > 0 && target.duration > 0) {
              const progress = ((target.currentTime / target.duration) * 100).toFixed(0);
              setAudioDebugInfo(`Playing ${progress}% (${target.currentTime.toFixed(1)}s/${target.duration.toFixed(1)}s) vol=${target.volume.toFixed(1)}`);
            }
          }}
          onError={(e) => {
            const target = e.currentTarget;
            const errorCode = target.error?.code || 0;
            const errorNames = ['', 'ABORTED', 'NETWORK', 'DECODE', 'SRC_NOT_SUPPORTED'];
            const errorName = errorNames[errorCode] || 'UNKNOWN';
            const debugMsg = `ERROR: ${errorName} (code ${errorCode}), network=${target.networkState}, ready=${target.readyState}`;
            setAudioDebugInfo(debugMsg);

            logger.error('Audio playback error:', {
              error: e,
              src: target.src,
              networkState: target.networkState,
              readyState: target.readyState,
              errorCode: target.error?.code,
              errorMessage: target.error?.message,
              userAgent: navigator.userAgent
            });

            // Include detailed error in the message
            setError(`Audio Error: ${errorName} (code ${errorCode}). Network: ${target.networkState}, Ready: ${target.readyState}. Try refreshing the page.`);
          }}
          style={isMobile ? {
            width: '100%',
            marginTop: 8,
            display: 'block',
            position: 'fixed',
            bottom: 60,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
            maxWidth: 'calc(90% - 32px)'
          } : {
            display: 'none'
          }}
        />
      )}

      {/* Summary Report Dialog */}
      <Dialog
        open={summaryDialogOpen}
        onClose={() => setSummaryDialogOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Call History Summary - {summarizeBy === 'extension' ? 'By Extension' : 'By User'}
            </Typography>
            <IconButton onClick={() => setSummaryDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {summaryLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : summaryData.length > 0 ? (
            <>
              <Typography variant="body2" color="textSecondary" mb={2}>
                Period: {startDate ? format(startDate, 'MMM d, yyyy') : 'N/A'} - {endDate ? format(endDate, 'MMM d, yyyy') : 'N/A'}
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }}>{summarizeBy === 'extension' ? 'Extension' : 'User'}</TableCell>
                      <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }}>Department</TableCell>
                      <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }}>Site</TableCell>
                      <TableCell rowSpan={2} align="right" sx={{ fontWeight: 'bold' }}>Calls</TableCell>
                      <TableCell align="center" colSpan={2} sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'divider', backgroundColor: 'action.hover' }}>
                        Total
                      </TableCell>
                      <TableCell align="center" colSpan={3} sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'divider' }}>
                        Inbound
                      </TableCell>
                      <TableCell align="center" colSpan={3} sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'divider' }}>
                        Outbound
                      </TableCell>
                      <TableCell align="center" colSpan={3} sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'divider' }}>
                        Internal
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', backgroundColor: 'action.hover' }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', backgroundColor: 'action.hover' }}>Avg</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Calls</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Avg</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Calls</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Avg</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Calls</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>Avg</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {row.label}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize="0.8rem" color="text.secondary">
                            {row.department || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize="0.8rem" color="text.secondary">
                            {row.site || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">{row.total_calls}</Typography>
                        </TableCell>
                        {/* Total Duration */}
                        <TableCell align="right" sx={{ backgroundColor: 'action.hover' }}>
                          <Typography variant="body2" fontWeight="medium">{row.total_duration_formatted}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary', backgroundColor: 'action.hover' }}>
                          {row.avg_duration_formatted}
                        </TableCell>
                        {/* Inbound */}
                        <TableCell align="right">{row.inbound_calls}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">{row.inbound_duration_formatted}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {row.avg_inbound_duration_formatted}
                        </TableCell>
                        {/* Outbound */}
                        <TableCell align="right">{row.outbound_calls}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">{row.outbound_duration_formatted}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {row.avg_outbound_duration_formatted}
                        </TableCell>
                        {/* Internal */}
                        <TableCell align="right">{row.internal_calls}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">{row.internal_duration_formatted}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {row.avg_internal_duration_formatted}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Overall Totals Row */}
                    {summaryData.length > 1 && (
                      <TableRow sx={{ backgroundColor: 'action.selected' }}>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" fontWeight="bold">
                            OVERALL TOTALS
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {summaryData.reduce((sum, row) => sum + row.total_calls, 0)}
                          </Typography>
                        </TableCell>
                        {/* Total Duration */}
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {(() => {
                              const totalSeconds = summaryData.reduce((sum, row) => sum + row.total_duration, 0);
                              const hours = Math.floor(totalSeconds / 3600);
                              const minutes = Math.floor((totalSeconds % 3600) / 60);
                              const seconds = totalSeconds % 60;
                              return hours > 0 ? `${hours}h ${minutes}m ${seconds}s` : `${minutes}m ${seconds}s`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {(() => {
                              const totalCalls = summaryData.reduce((sum, row) => sum + row.total_calls, 0);
                              const totalSeconds = summaryData.reduce((sum, row) => sum + row.total_duration, 0);
                              const avgSeconds = totalCalls > 0 ? Math.floor(totalSeconds / totalCalls) : 0;
                              const minutes = Math.floor(avgSeconds / 60);
                              const seconds = avgSeconds % 60;
                              return `${minutes}m ${seconds}s`;
                            })()}
                        </TableCell>
                        {/* Inbound Totals */}
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {summaryData.reduce((sum, row) => sum + row.inbound_calls, 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {(() => {
                              const totalSeconds = summaryData.reduce((sum, row) => sum + row.inbound_duration, 0);
                              const hours = Math.floor(totalSeconds / 3600);
                              const minutes = Math.floor((totalSeconds % 3600) / 60);
                              const seconds = totalSeconds % 60;
                              return hours > 0 ? `${hours}h ${minutes}m ${seconds}s` : `${minutes}m ${seconds}s`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {(() => {
                              const totalCalls = summaryData.reduce((sum, row) => sum + row.inbound_calls, 0);
                              const totalSeconds = summaryData.reduce((sum, row) => sum + row.inbound_duration, 0);
                              const avgSeconds = totalCalls > 0 ? Math.floor(totalSeconds / totalCalls) : 0;
                              const minutes = Math.floor(avgSeconds / 60);
                              const seconds = avgSeconds % 60;
                              return `${minutes}m ${seconds}s`;
                            })()}
                        </TableCell>
                        {/* Outbound Totals */}
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {summaryData.reduce((sum, row) => sum + row.outbound_calls, 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {(() => {
                              const totalSeconds = summaryData.reduce((sum, row) => sum + row.outbound_duration, 0);
                              const hours = Math.floor(totalSeconds / 3600);
                              const minutes = Math.floor((totalSeconds % 3600) / 60);
                              const seconds = totalSeconds % 60;
                              return hours > 0 ? `${hours}h ${minutes}m ${seconds}s` : `${minutes}m ${seconds}s`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {(() => {
                              const totalCalls = summaryData.reduce((sum, row) => sum + row.outbound_calls, 0);
                              const totalSeconds = summaryData.reduce((sum, row) => sum + row.outbound_duration, 0);
                              const avgSeconds = totalCalls > 0 ? Math.floor(totalSeconds / totalCalls) : 0;
                              const minutes = Math.floor(avgSeconds / 60);
                              const seconds = avgSeconds % 60;
                              return `${minutes}m ${seconds}s`;
                            })()}
                        </TableCell>
                        {/* Internal Totals */}
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {summaryData.reduce((sum, row) => sum + row.internal_calls, 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {(() => {
                              const totalSeconds = summaryData.reduce((sum, row) => sum + row.internal_duration, 0);
                              const hours = Math.floor(totalSeconds / 3600);
                              const minutes = Math.floor((totalSeconds % 3600) / 60);
                              const seconds = totalSeconds % 60;
                              return hours > 0 ? `${hours}h ${minutes}m ${seconds}s` : `${minutes}m ${seconds}s`;
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {(() => {
                              const totalCalls = summaryData.reduce((sum, row) => sum + row.internal_calls, 0);
                              const totalSeconds = summaryData.reduce((sum, row) => sum + row.internal_duration, 0);
                              const avgSeconds = totalCalls > 0 ? Math.floor(totalSeconds / totalCalls) : 0;
                              const minutes = Math.floor(avgSeconds / 60);
                              const seconds = avgSeconds % 60;
                              return `${minutes}m ${seconds}s`;
                            })()}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity="info">No data available for the selected filters.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CallHistory;